import {
  Fragment,
  isValidElement,
  lazy,
  Suspense,
  type FC,
  type ReactElement,
  useMemo,
  useId,
  useEffect
} from "react"

import type {
  WithCapsuleCardFragment,
  WithCapsuleImageFragment,
  WithCapsuleMultiImageFragment
} from "~/contentful"
import classNames from "classnames"
import type { CapsuleProps } from "~/components/capsules"
import MultiImageItem from "~/components/capsules/MultiImageItem"
import MultiImageCard from "~/components/capsules/MultiImageCard"
import { getResponsiveSizes } from "~/helpers/responsiveImage"

const MultiImageCarouselLazy = lazy(
  () => import("~/components/capsules/MultiImageCarousel")
)

interface Props extends CapsuleProps {
  capsule: WithCapsuleMultiImageFragment
}

const OnResolved: FC<{ onResolved: () => void }> = ({ onResolved }) => {
  useEffect(onResolved, [onResolved])
  return null
}

const MultiImage: FC<Props> = ({
  capsule,
  page,
  index,
  serverRender,
  onResolved
}) => {
  const uniqueId = useId()
  const shouldEagerLoad = index !== undefined && index < 3
  const isLCP = capsule.isMainContent === true
  const itemCount = capsule.itemsCollection?.items.length ?? 0
  const needsMobileContainerPadding =
    itemCount === 1 || capsule.multipleLinesMobile === true

  const applyMobileHorizontalPadding =
    needsMobileContainerPadding &&
    !(capsule.fullWidth && (capsule.multipleLinesMobile || itemCount === 1))

  const fullWidthSpacingGutter =
    capsule.fullWidth === true && capsule.spacing === true

  const containerClassNames = useMemo(
    () =>
      classNames({
        "w-full": capsule.fullWidth,
        "px-4": fullWidthSpacingGutter,
        "px-4 sm:px-0": applyMobileHorizontalPadding && !fullWidthSpacingGutter,
        "mx-auto": !capsule.fullWidth,
        "max-w-camp-container-mobile lg:max-w-camp-container":
          !capsule.cardMaxWidth && !capsule.fullWidth,
        "lg:max-w-[1014px]":
          capsule.cardMaxWidth == "75%" && !capsule.fullWidth,
        "lg:max-w-[676px]": capsule.cardMaxWidth == "50%" && !capsule.fullWidth,
        "lg:max-w-[338px]": capsule.cardMaxWidth == "25%" && !capsule.fullWidth
      }),
    [capsule, applyMobileHorizontalPadding, fullWidthSpacingGutter]
  )

  const sizeMobile = useMemo(
    () => `${100 / (capsule.visibleImagesMobile || 1)}vw`,
    [capsule.visibleImagesMobile]
  )

  const sizeDesktop = useMemo(
    () => `${100 / (capsule.visibleImagesDesktop || 1)}vw`,
    [capsule.visibleImagesDesktop]
  )

  // Determine container type based on cardMaxWidth and fullWidth settings
  const containerType = useMemo(() => {
    if (capsule.fullWidth) return undefined
    if (!capsule.cardMaxWidth) return "camp-container"
    // For cardMaxWidth percentages, use the closest container size
    if (capsule.cardMaxWidth === "75%") return "camp-container"
    if (capsule.cardMaxWidth === "50%") return "camp-container-mobile"
    if (capsule.cardMaxWidth === "25%") return "camp-container-mobile"
    return "camp-container"
  }, [capsule.fullWidth, capsule.cardMaxWidth])

  // Calculate responsive sizes accounting for containers
  const responsiveSizes = useMemo(
    () =>
      getResponsiveSizes(
        sizeMobile,
        sizeDesktop,
        containerType,
        capsule.fullWidth || false
      ),
    [sizeMobile, sizeDesktop, containerType, capsule.fullWidth]
  )

  const renderItem = (
    item?: WithCapsuleCardFragment | WithCapsuleImageFragment | null,
    itemSizeMobile: string = sizeMobile,
    itemSizeDesktop: string = sizeDesktop,
    itemSizes: string = responsiveSizes
  ) => {
    if (!item) return null

    if (item.__typename === "CapsuleImage") {
      return (
        <MultiImageItem
          item={item as WithCapsuleImageFragment}
          origin={capsule.name ?? ""}
          sizeMobile={itemSizeMobile}
          sizeDesktop={itemSizeDesktop}
          sizes={itemSizes}
          isMainContent={isLCP}
          shouldEagerLoad={shouldEagerLoad}
        />
      )
    }

    if (item.__typename === "CapsuleCard") {
      return (
        <MultiImageCard
          item={item}
          origin={capsule.name ?? ""}
          spacing={capsule.spacing || false}
          fullWidth={capsule.fullWidth ?? false}
          sizeMobile={itemSizeMobile}
          sizeDesktop={itemSizeDesktop}
          sizes={itemSizes}
          isMainContent={isLCP}
          shouldEagerLoad={shouldEagerLoad}
        />
      )
    }

    return null
  }

  // Single item layout
  if (itemCount === 1) {
    const item = capsule.itemsCollection?.items[0]
    const singleItemSizes = getResponsiveSizes(
      "100vw",
      "100vw",
      containerType,
      capsule.fullWidth || false
    )

    return (
      <div className={containerClassNames}>
        {renderItem(item, "100vw", "100vw", singleItemSizes)}
      </div>
    )
  }

  // Grid layout (multiple lines on mobile or desktop)
  if (capsule.multipleLinesMobile || capsule.multipleLinesDesktop) {
    const gridId = `multiimage-grid-${capsule.sys?.id ?? uniqueId}`
    const mobileColumns = capsule.visibleImagesMobile || 1
    const desktopColumns = capsule.visibleImagesDesktop || 1

    return (
      <div className={containerClassNames}>
        <style
          dangerouslySetInnerHTML={{
            __html: `
          #${gridId} {
            display: grid;
            grid-template-columns: repeat(${mobileColumns}, minmax(0, 1fr));
            gap: ${capsule.spacing ? "1.5rem" : "0"};
          }
          @media (min-width: 640px) {
            #${gridId} {
              grid-template-columns: repeat(${desktopColumns}, minmax(0, 1fr));
            }
          }
        `
          }}
        />
        <div id={gridId}>
          {capsule.itemsCollection?.items.map((item, index) => (
            <Fragment key={index}>{renderItem(item)}</Fragment>
          ))}
        </div>
      </div>
    )
  }

  // Carousel layout
  const slidesPerViewMobile = capsule.visibleImagesMobile ?? 1
  const slidesPerViewDesktop = capsule.visibleImagesDesktop ?? 1
  const carouselItems = (capsule.itemsCollection?.items ?? [])
    .map((item, itemIndex) =>
      item == null ? null : (
        <Fragment key={`${item.__typename ?? "item"}-${itemIndex}`}>
          {renderItem(item)}
        </Fragment>
      )
    )
    .filter((item): item is ReactElement => item != null)

  // Pre-hydration slide sizing: Swiper sets widths in JS after mount, so before
  // that runs slides take their content width (full-size image) and have no
  // gap. Mirror Swiper's breakpoint math here so the SSR'd markup paints
  // correctly until Swiper overrides these widths inline.
  const preLayoutContainerId = `multiimage-carousel-${(capsule.sys?.id ?? uniqueId).replace(/[^a-zA-Z0-9_-]/g, "")}`
  const preLayoutSpacingPx = capsule.spacing ? 24 : 0
  const preLayoutMobileSlides = Math.max(slidesPerViewMobile, 1)
  const preLayoutDesktopSlides = Math.max(slidesPerViewDesktop, 1)
  const preLayoutCss = `
    #${preLayoutContainerId} .swiper-wrapper { display: flex; }
    #${preLayoutContainerId} .swiper-slide {
      flex-shrink: 0;
      box-sizing: border-box;
      width: calc((100% - ${preLayoutSpacingPx * (preLayoutMobileSlides - 1)}px) / ${preLayoutMobileSlides});
      margin-right: ${preLayoutSpacingPx}px;
    }
    #${preLayoutContainerId} .swiper-slide:last-child { margin-right: 0; }
    @media (min-width: 640px) {
      #${preLayoutContainerId} .swiper-slide {
        width: calc((100% - ${preLayoutSpacingPx * (preLayoutDesktopSlides - 1)}px) / ${preLayoutDesktopSlides});
        margin-right: ${preLayoutSpacingPx}px;
      }
    }
  `

  return (
    <div className={classNames("relative", containerClassNames)}>
      <Suspense
        fallback={
          serverRender ? (
            <div id={preLayoutContainerId}>
              <style dangerouslySetInnerHTML={{ __html: preLayoutCss }} />
              <div className="swiper">
                <div className="swiper-wrapper">
                  {carouselItems.map((item, itemIndex) => (
                    <div
                      key={
                        isValidElement(item) && item.key != null
                          ? item.key
                          : itemIndex
                      }
                      className="swiper-slide"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full h-64" />
          )
        }
      >
        <MultiImageCarouselLazy
          customColor={page.color1}
          showArrowsMobile={capsule.showArrowsMobile ?? false}
          showArrowsDesktop={capsule.showArrowsDesktop ?? false}
          showSliderMobile={capsule.showSliderMobile ?? false}
          showSliderDesktop={capsule.showSliderDesktop ?? false}
          showDotsMobile={capsule.showDotsMobile}
          showDotsDesktop={capsule.showDotsDesktop}
          slidesPerViewMobile={slidesPerViewMobile}
          slidesPerViewDesktop={slidesPerViewDesktop}
          bleedMobile={capsule.bleedMobile ?? false}
          bleedDesktop={capsule.bleedDesktop ?? false}
          spacing={capsule.spacing ?? false}
          fullWidth={capsule.fullWidth ?? false}
          items={carouselItems}
        />
        {onResolved && <OnResolved onResolved={onResolved} />}
      </Suspense>
    </div>
  )
}

MultiImage.displayName = "MultiImage"

export default MultiImage
